import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/lib/LanguageContext";
import { pickLocalized, dateLocale } from "@/lib/i18n";
import { ASSETS } from "@/lib/content";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenantPath";
import { Link } from "react-router-dom";

const COVERS = [ASSETS.mortar, ASSETS.apothecary, ASSETS.handMassage];

const monthLabel = (d, lang) => {
  const dt = new Date(d);
  return dt.toLocaleString(dateLocale(lang), { month: "long", year: "numeric" });
};

const Blog = () => {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.slice(0, 3).map((p, i) => ({
            slug: p.slug,
            tag: pickLocalized(p, "tag", lang),
            title: pickLocalized(p, "title", lang),
            excerpt: pickLocalized(p, "excerpt", lang),
            date: monthLabel(p.created_at, lang),
            cover: p.cover_url || COVERS[i % COVERS.length],
          }));
          setPosts(mapped);
        }
      } catch (e) {
        // fallback to static content on the public site
        if (process.env.NODE_ENV !== "production") console.debug("[blog] remote load failed, using static:", e?.message);
      }
    })();
  }, [lang]);

  const items = posts || t.blog.items.map((it, i) => ({ ...it, cover: COVERS[i % COVERS.length] }));
  return (
    <section id="blog" data-testid="blog-section" className="relative py-24 md:py-36 bg-eh-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">{t.blog.eyebrow}</span>
            <h2 className="font-display text-5xl md:text-6xl text-eh-ink mt-4 leading-[1.05] whitespace-pre-line">
              {t.blog.title}
            </h2>
          </div>
          <p className="col-span-12 md:col-span-5 text-lg md:text-xl text-eh-muted leading-relaxed">{t.blog.lead}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((post, i) => {
            const Wrapper = post.slug ? Link : "div";
            const wrapperProps = post.slug ? { to: tenantHref(`/journal/${post.slug}`) } : {};
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`blog-card-${i}`}
                className="group"
              >
                <Wrapper {...wrapperProps} className={post.slug ? "block cursor-pointer" : "block"}>
              <div className="overflow-hidden rounded-[1.75rem] aspect-[4/5] mb-5">
                <img
                  src={post.cover || COVERS[i % COVERS.length]}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-eh-muted">
                <span className="text-eh-sage-deep font-semibold">{post.tag}</span>
                <span className="w-1 h-1 rounded-full bg-eh-border" />
                <span>{post.date}</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-eh-ink mt-3 leading-tight group-hover:text-eh-sage-deep transition-colors">
                {post.title}
              </h3>
              <p className="text-eh-muted mt-2 leading-relaxed">{post.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-eh-ink group-hover:gap-3 transition-all">
                {t.blog.readMore}
                <ArrowUpRight size={16} />
              </div>
                </Wrapper>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-16 flex justify-center">
          <Link
            to={tenantHref("/journal")}
            data-testid="blog-view-all"
            className="btn-ghost"
          >
            {t.blog.viewAll}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Blog;
